import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Settings, Save, Star, Shield,
  AlertCircle, Award, CheckCircle, Info,
  Zap, TrendingUp, Store, Video, Clock, X
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import {
  getAllMonitoringConfigs,
  updateMonitoringConfig,
  getAllMonitoringData,
} from "../../redux/Actions/ProfessionalMonitoringActions";
import {
  getAllAchievementConfigs,
  updateAchievementConfig,
} from "../../redux/Actions/AchievementActions";
import { RootState, AppDispatch } from "../../redux/store";

// Achievement Config Modal Component
const AchievementConfigModal = ({ isOpen, onClose, achievementConfigs, selectedAchievementType, setSelectedAchievementType, dispatch }) => {
  const config = achievementConfigs.find((c) => c.configKey === selectedAchievementType);
  
  const [formData, setFormData] = useState(config?.thresholds || {});

  useEffect(() => {
    const currentConfig = achievementConfigs.find((c) => c.configKey === selectedAchievementType);
    if (currentConfig) {
      setFormData(currentConfig.thresholds);
    }
  }, [selectedAchievementType, achievementConfigs]);

  if (!isOpen) return null;

  const handleSave = async () => {
    try {
      await dispatch(updateAchievementConfig({
        configKey: selectedAchievementType,
        thresholds: formData
      })).unwrap();
      await dispatch(getAllAchievementConfigs());
      onClose();
    } catch (error) {
      // Silent error handling
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Achievement Configuration</h2>
              <p className="text-sm text-gray-600 mt-1">
                Set thresholds for {selectedAchievementType === "professional" ? "Professional" : "Client"} achievements
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Type Switcher */}
          <div className="flex gap-2 mt-4">
            <Button
              variant={selectedAchievementType === "professional" ? "default" : "outline"}
              onClick={() => setSelectedAchievementType("professional")}
              className="flex-1"
            >
              <Award className="h-4 w-4 mr-2" />
              Professional
            </Button>
            <Button
              variant={selectedAchievementType === "client" ? "default" : "outline"}
              onClick={() => setSelectedAchievementType("client")}
              className="flex-1"
            >
              <Star className="h-4 w-4 mr-2" />
              Client
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-6" style={{ textAlign: "left" }}>
          {selectedAchievementType === "professional" ? (
            <>
              {/* Professional Achievements */}
              <div>
                <h3 className="font-semibold text-lg mb-4">Professional Achievement Thresholds</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Top Earner - Amount (€)</label>
                    <input
                      type="number"
                      value={formData.topEarnerAmount || 10000}
                      onChange={(e) => setFormData({...formData, topEarnerAmount: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum earnings to unlock Top Earner</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Expert Professional - Projects</label>
                    <input
                      type="number"
                      value={formData.expertProjectsCount || 10}
                      onChange={(e) => setFormData({...formData, expertProjectsCount: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Completed projects to unlock Expert</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Rising Star - Projects</label>
                    <input
                      type="number"
                      value={formData.risingStarProjectsCount || 5}
                      onChange={(e) => setFormData({...formData, risingStarProjectsCount: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Projects needed for Rising Star</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Rising Star - Min Rating</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.risingStarMinRating || 4.5}
                      onChange={(e) => setFormData({...formData, risingStarMinRating: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="0"
                      max="5"
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum rating for Rising Star</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Highly Rated - Reviews</label>
                    <input
                      type="number"
                      value={formData.highlyRatedReviewsCount || 10}
                      onChange={(e) => setFormData({...formData, highlyRatedReviewsCount: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Reviews needed for Highly Rated</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Highly Rated - Min Rating</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.highlyRatedMinRating || 4.7}
                      onChange={(e) => setFormData({...formData, highlyRatedMinRating: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="0"
                      max="5"
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum rating for Highly Rated</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Client Achievements */}
              <div>
                <h3 className="font-semibold text-lg mb-4">Client Achievement Thresholds</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Top Client - Spent Amount (€)</label>
                    <input
                      type="number"
                      value={formData.topClientSpentAmount || 10000}
                      onChange={(e) => setFormData({...formData, topClientSpentAmount: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum spending for Top Client</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Top Client - Success Rate (%)</label>
                    <input
                      type="number"
                      value={formData.topClientSuccessRate || 80}
                      onChange={(e) => setFormData({...formData, topClientSuccessRate: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="0"
                      max="100"
                    />
                    <p className="text-xs text-gray-500 mt-1">Required success rate for Top Client</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Power Client - Projects</label>
                    <input
                      type="number"
                      value={formData.powerClientProjectsCount || 10}
                      onChange={(e) => setFormData({...formData, powerClientProjectsCount: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Completed projects for Power Client</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Team Builder - Projects</label>
                    <input
                      type="number"
                      value={formData.teamBuilderProjectsCount || 5}
                      onChange={(e) => setFormData({...formData, teamBuilderProjectsCount: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Projects needed for Team Builder</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Team Builder - Success Rate (%)</label>
                    <input
                      type="number"
                      value={formData.teamBuilderSuccessRate || 90}
                      onChange={(e) => setFormData({...formData, teamBuilderSuccessRate: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="0"
                      max="100"
                    />
                    <p className="text-xs text-gray-500 mt-1">Required success rate for Team Builder</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Trusted Reviewer - Reviews</label>
                    <input
                      type="number"
                      value={formData.trustedReviewerCount || 10}
                      onChange={(e) => setFormData({...formData, trustedReviewerCount: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Reviews needed for Trusted Reviewer</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

const ScoringSettings = () => {
  const dispatch = useDispatch<AppDispatch>();
  
  const {
    configs: monitoringConfigs,
    allMonitoringData,
    monitoringPagination,
    loading,
  } = useSelector((state: RootState) => state.professionalMonitoring);

  const {
    allConfigs: achievementConfigs,
  } = useSelector((state: RootState) => state.achievements);

  const [showMonitoringModal, setShowMonitoringModal] = useState(false);
  const [showAchievementModal, setShowAchievementModal] = useState(false);
  const [selectedConfigKey, setSelectedConfigKey] = useState<"home_service" | "remote">("home_service");
  const [selectedAchievementType, setSelectedAchievementType] = useState<"professional" | "client">("professional");

  useEffect(() => {
    dispatch(getAllMonitoringConfigs());
    // Fetch all monitoring data without pagination for stats (set a high limit)
    dispatch(getAllMonitoringData({ page: 1, limit: 9999 }));
    dispatch(getAllAchievementConfigs());
  }, [dispatch]);

  // Performance Tiers (fixed, based on trust scores)
  const performanceTiers = [
    { name: "Basic", score: "0-399", color: "gray", description: "Starting tier for new professionals" },
    { name: "Standard", score: "400-599", color: "blue", description: "Good performance and compliance" },
    { name: "Advanced", score: "600-799", color: "green", description: "Excellent performance record" },
    { name: "Elite", score: "800+", color: "yellow", description: "Top-tier professionals with outstanding records" },
  ];
  
  // Calculate real-time stats from monitoring data
  // Use pagination total count for accurate total professionals count
  const stats = {
    totalProfessionals: monitoringPagination?.totalCount || allMonitoringData?.length || 0,
    activeProfessionals: allMonitoringData?.filter(p => !p.isSuspended)?.length || 0,
    suspendedProfessionals: allMonitoringData?.filter(p => p.isSuspended)?.length || 0,
    averageTrustScore: allMonitoringData?.length > 0 
      ? Math.round(allMonitoringData.reduce((sum, p) => sum + p.trustScore, 0) / allMonitoringData.length)
      : 0,
    eliteProfessionals: allMonitoringData?.filter(p => p.performanceTier === "Elite")?.length || 0,
  };

  // Loading state
  if (loading && !monitoringConfigs.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading monitoring settings...</p>
        </div>
      </div>
    );
  }

  // Automated Monitoring Modal Component
  const MonitoringConfigModal = ({ isOpen, onClose }) => {
    const config = monitoringConfigs.find((c) => c.configKey === selectedConfigKey);
    
    const [formData, setFormData] = useState(config || {
      consecutiveVisitsWarningThreshold: 3,
      totalVisitsForConversionCheck: 10,
      minimumContractsRequired: 3,
      consecutiveVideoCallsWarningThreshold: 5,
      totalVideoCallsForConversionCheck: 10,
      minimumConversionRatePercent: 20,
      minimumCompletionRatePercent: 30,
      reportsForFirstWarning: 1,
      reportsForSuspension: 2,
      reportsForPermanentBan: 3,
      contactSharingForFirstWarning: 1,
      contactSharingForSuspension: 2,
      contactSharingForBan: 3,
      consecutiveRefundRequestsForSuspension: 3,
      firstSuspensionDays: 7,
      secondSuspensionDays: 14,
      pointsDeductedPerWarning: -25,
      trustBonusPoints: 20,
      compliantMonthsForBonus: 3,
      warningRepeatDays: 7,
      monthlyResetDays: 30,
      categoryRevenueThresholds: {},
    });

    useEffect(() => {
      const currentConfig = monitoringConfigs.find((c) => c.configKey === selectedConfigKey);
      if (currentConfig) {
        setFormData(currentConfig);
      }
    }, [selectedConfigKey]);

    if (!isOpen) return null;

    const handleSave = async () => {
      try {
        await dispatch(updateMonitoringConfig({
          configKey: selectedConfigKey,
          data: formData
        })).unwrap();
        await dispatch(getAllMonitoringConfigs());
        onClose();
      } catch (error) {
        // Silent error handling
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b sticky top-0 bg-white z-10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Automated Monitoring Configuration</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Configure thresholds and rules for {selectedConfigKey === "home_service" ? "Home Service" : "Remote"} Professionals
                </p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Config Type Switcher */}
            <div className="flex gap-2 mt-4">
              <Button
                variant={selectedConfigKey === "home_service" ? "default" : "outline"}
                onClick={() => setSelectedConfigKey("home_service")}
                className="flex-1"
              >
                <Store className="h-4 w-4 mr-2" />
                Home Service
              </Button>
              <Button
                variant={selectedConfigKey === "remote" ? "default" : "outline"}
                onClick={() => setSelectedConfigKey("remote")}
                className="flex-1"
              >
                <Video className="h-4 w-4 mr-2" />
                Remote/Video
              </Button>
            </div>
          </div>

          <div className="p-6 space-y-8" style={{ textAlign: "left" }}>
            {/* Home Service Settings */}
            {selectedConfigKey === "home_service" && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Store className="h-5 w-5 text-blue-600" />
                    Home Visit Monitoring
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Consecutive Visits Warning</label>
                      <input
                        type="number"
                        value={formData.consecutiveVisitsWarningThreshold}
                        onChange={(e) => setFormData({...formData, consecutiveVisitsWarningThreshold: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Visits without contract before warning</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Total Visits for Check</label>
                      <input
                        type="number"
                        value={formData.totalVisitsForConversionCheck}
                        onChange={(e) => setFormData({...formData, totalVisitsForConversionCheck: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Visits before checking conversion rate</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Minimum Contracts Required</label>
                      <input
                        type="number"
                        value={formData.minimumContractsRequired}
                        onChange={(e) => setFormData({...formData, minimumContractsRequired: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Minimum contracts per period</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Video Call Settings */}
            {selectedConfigKey === "remote" && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Video className="h-5 w-5 text-blue-600" />
                    Video Call Monitoring
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Consecutive Calls Warning</label>
                      <input
                        type="number"
                        value={formData.consecutiveVideoCallsWarningThreshold}
                        onChange={(e) => setFormData({...formData, consecutiveVideoCallsWarningThreshold: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Video calls without contract before warning</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Total Calls for Check</label>
                      <input
                        type="number"
                        value={formData.totalVideoCallsForConversionCheck}
                        onChange={(e) => setFormData({...formData, totalVideoCallsForConversionCheck: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Video calls before checking conversion</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Min Conversion Rate (%)</label>
                      <input
                        type="number"
                        value={formData.minimumConversionRatePercent}
                        onChange={(e) => setFormData({...formData, minimumConversionRatePercent: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="0"
                        max="100"
                      />
                      <p className="text-xs text-gray-500 mt-1">Minimum conversion rate percentage</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Min Completion Rate (%)</label>
                      <input
                        type="number"
                        value={formData.minimumCompletionRatePercent}
                        onChange={(e) => setFormData({...formData, minimumCompletionRatePercent: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="0"
                        max="100"
                      />
                      <p className="text-xs text-gray-500 mt-1">Minimum job completion rate</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Violation Thresholds */}
            <div>
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                Violation Thresholds
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <label className="block text-sm font-medium mb-2">Reports for First Warning</label>
                  <input
                    type="number"
                    value={formData.reportsForFirstWarning}
                    onChange={(e) => setFormData({...formData, reportsForFirstWarning: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <label className="block text-sm font-medium mb-2">Reports for Suspension</label>
                  <input
                    type="number"
                    value={formData.reportsForSuspension}
                    onChange={(e) => setFormData({...formData, reportsForSuspension: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <label className="block text-sm font-medium mb-2">Reports for Permanent Ban</label>
                  <input
                    type="number"
                    value={formData.reportsForPermanentBan}
                    onChange={(e) => setFormData({...formData, reportsForPermanentBan: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <label className="block text-sm font-medium mb-2">Contact Sharing: Warning</label>
                  <input
                    type="number"
                    value={formData.contactSharingForFirstWarning}
                    onChange={(e) => setFormData({...formData, contactSharingForFirstWarning: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <label className="block text-sm font-medium mb-2">Contact Sharing: Suspension</label>
                  <input
                    type="number"
                    value={formData.contactSharingForSuspension}
                    onChange={(e) => setFormData({...formData, contactSharingForSuspension: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <label className="block text-sm font-medium mb-2">Contact Sharing: Ban</label>
                  <input
                    type="number"
                    value={formData.contactSharingForBan}
                    onChange={(e) => setFormData({...formData, contactSharingForBan: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                </div>
              </div>
            </div>

            {/* Refund Request Monitoring */}
            <div>
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-purple-600" />
                Refund Request Monitoring
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-purple-50 rounded-lg">
                  <label className="block text-sm font-medium mb-2">Consecutive Refunds for Suspension</label>
                  <input
                    type="number"
                    value={formData.consecutiveRefundRequestsForSuspension || 3}
                    onChange={(e) => setFormData({...formData, consecutiveRefundRequestsForSuspension: parseInt(e.target.value) || 3})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Consecutive refund requests before 7-day suspension</p>
                </div>
              </div>
            </div>

            {/* Suspension Durations */}
            <div>
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-600" />
                Suspension Durations
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">First Suspension (days)</label>
                  <input
                    type="number"
                    value={formData.firstSuspensionDays}
                    onChange={(e) => setFormData({...formData, firstSuspensionDays: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Second Suspension (days)</label>
                  <input
                    type="number"
                    value={formData.secondSuspensionDays}
                    onChange={(e) => setFormData({...formData, secondSuspensionDays: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                </div>
              </div>
            </div>

            {/* Point System */}
            <div>
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Award className="h-5 w-5 text-green-600" />
                Trust Score & Points
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Points per Warning</label>
                  <input
                    type="number"
                    value={formData.pointsDeductedPerWarning}
                    onChange={(e) => setFormData({...formData, pointsDeductedPerWarning: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Negative value for deduction</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Trust Bonus Points</label>
                  <input
                    type="number"
                    value={formData.trustBonusPoints}
                    onChange={(e) => setFormData({...formData, trustBonusPoints: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Bonus for compliant behavior</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Compliant Months for Bonus</label>
                  <input
                    type="number"
                    value={formData.compliantMonthsForBonus}
                    onChange={(e) => setFormData({...formData, compliantMonthsForBonus: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Months before bonus award</p>
                </div>
              </div>
            </div>

            {/* System Settings */}
            <div>
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5 text-gray-600" />
                System Settings
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Warning Repeat Days</label>
                  <input
                    type="number"
                    value={formData.warningRepeatDays}
                    onChange={(e) => setFormData({...formData, warningRepeatDays: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Days before showing same warning again</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Monthly Reset Days</label>
                  <input
                    type="number"
                    value={formData.monthlyResetDays}
                    onChange={(e) => setFormData({...formData, monthlyResetDays: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Days before monthly counter reset</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="pt-4 border-t flex justify-end gap-3">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleSave}>
                <Save className="h-4 w-4 mr-2" />
                Save Configuration
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Professional Monitoring Settings</h1>
                <p className="text-gray-600 mt-1">Configure automated monitoring rules and thresholds</p>
              </div>
            </div>

            {/* Overview Stats */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">System Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="space-y-2">
                  <div className="text-sm text-gray-500">Total Professionals</div>
                  <div className="text-2xl font-semibold">{stats.totalProfessionals}</div>
                  <div className="text-sm text-gray-600">Being monitored</div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-gray-500">Active</div>
                  <div className="text-2xl font-semibold text-green-600">{stats.activeProfessionals}</div>
                  <div className="text-sm text-gray-600">In good standing</div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-gray-500">Suspended</div>
                  <div className="text-2xl font-semibold text-red-600">{stats.suspendedProfessionals}</div>
                  <div className="text-sm text-gray-600">Temporarily restricted</div>
                </div>
                <div className="space-y-2">
                  <div className="text-sm text-gray-500">Elite Tier</div>
                  <div className="text-2xl font-semibold text-yellow-600">{stats.eliteProfessionals}</div>
                  <div className="text-sm text-gray-600">Top performers</div>
                </div>
              </div>
            </Card>

            {/* Performance Tiers */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Performance Tiers</h2>
              <p className="text-sm text-gray-600 mb-4">
                Tiers are automatically calculated based on trust scores. Professionals earn points by completing contracts, maintaining compliance, and providing quality service.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {performanceTiers.map((tier, index) => {
                  const getTierStyles = (color: string) => {
                    const colorMap = {
                      "yellow": "bg-yellow-50 border-yellow-200 text-yellow-600",
                      "green": "bg-green-50 border-green-200 text-green-600",
                      "blue": "bg-blue-50 border-blue-200 text-blue-600",
                      "gray": "bg-gray-50 border-gray-200 text-gray-600"
                    };
                    return colorMap[color] || "bg-gray-50 border-gray-200 text-gray-600";
                  };
                  
                  const getTierIcon = (name: string) => {
                    switch (name) {
                      case "Elite": return <Award className="h-5 w-5" />;
                      case "Advanced": return <Shield className="h-5 w-5" />;
                      case "Standard": return <CheckCircle className="h-5 w-5" />;
                      case "Basic": return <Star className="h-5 w-5" />;
                      default: return <Award className="h-5 w-5" />;
                    }
                  };
                  
                  const professionalsInTier = allMonitoringData?.filter(p => p.performanceTier === tier.name)?.length || 0;
                  
                  return (
                    <div key={index} className={`p-4 rounded-lg border ${getTierStyles(tier.color)}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {getTierIcon(tier.name)}
                        <h3 className="font-medium">{tier.name}</h3>
                      </div>
                      <p className="text-sm mb-1">Score: {tier.score}</p>
                      <p className="text-xs opacity-75">{tier.description}</p>
                      <div className="mt-3 pt-3 border-t border-current/20">
                        <p className="text-xs">
                          <strong>{professionalsInTier}</strong> professional{professionalsInTier !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Automated Monitoring Settings */}
            <Card className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Automated Monitoring System</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Configure thresholds and rules for home service and remote professionals
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowMonitoringModal(true)}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Configure Rules
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Store className="h-5 w-5 text-blue-600" />
                    <h3 className="font-medium">Home Service Professionals</h3>
                  </div>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-600" />
                      <span>Tracks home visits and contracts</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-600" />
                      <span>Monitors conversion rates</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-600" />
                      <span>Revenue threshold checks</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-600" />
                      <span>Automatic warnings and suspensions</span>
                    </div>
                  </div>
                  {monitoringConfigs.find(c => c.configKey === "home_service") && (
                    <div className="mt-4 pt-3 border-t border-blue-200">
                      <p className="text-xs text-blue-700">
                        <strong>Warning Threshold:</strong> {monitoringConfigs.find(c => c.configKey === "home_service")?.consecutiveVisitsWarningThreshold} consecutive visits
                      </p>
                    </div>
                  )}
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Video className="h-5 w-5 text-purple-600" />
                    <h3 className="font-medium">Remote/Video Professionals</h3>
                  </div>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                      <span>Tracks video calls and contracts</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                      <span>Monitors conversion rates</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                      <span>Contact sharing detection</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                      <span>Job completion tracking</span>
                    </div>
                  </div>
                  {monitoringConfigs.find(c => c.configKey === "remote") && (
                    <div className="mt-4 pt-3 border-t border-purple-200">
                      <p className="text-xs text-purple-700">
                        <strong>Warning Threshold:</strong> {monitoringConfigs.find(c => c.configKey === "remote")?.consecutiveVideoCallsWarningThreshold} consecutive calls
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-orange-600" />
                      <h4 className="font-medium">Automated Checks</h4>
                    </div>
                    <Badge variant={undefined} className="bg-green-100 text-green-700">Active</Badge>
                  </div>
                  <p className="text-sm text-gray-600">Daily suspension checks, monthly resets, real-time violation detection</p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-blue-600" />
                      <h4 className="font-medium">Trust Score System</h4>
                    </div>
                    <Badge variant={undefined} className="bg-blue-100 text-blue-700">
                      Avg: {stats.averageTrustScore}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">+5 per completed contract, -25 per warning, +20 after 3 compliant months</p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Info className="h-5 w-5 text-gray-600" />
                      <h4 className="font-medium">Current Status</h4>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    {stats.activeProfessionals} active, {stats.suspendedProfessionals} suspended, {stats.eliteProfessionals} elite
                  </p>
                </div>
              </div>
            </Card>

            {/* Quick Actions */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Button
                  variant="outline"
                  onClick={() => window.location.href = "/admin/scoring/monitoring"}
                  className="h-auto py-4 justify-start"
                >
                  <div className="flex items-start gap-3 text-left">
                    <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h3 className="font-medium mb-1">View Monitoring Dashboard</h3>
                      <p className="text-sm text-gray-600">See all professionals&apos; monitoring data and client reports</p>
                    </div>
                  </div>
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setShowMonitoringModal(true)}
                  className="h-auto py-4 justify-start"
                >
                  <div className="flex items-start gap-3 text-left">
                    <Settings className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div>
                      <h3 className="font-medium mb-1">Configure Monitoring</h3>
                      <p className="text-sm text-gray-600">Adjust warning levels, suspension rules, and point values</p>
                    </div>
                  </div>
                </Button>

                <Button
                  variant="outline"
                  onClick={() => setShowAchievementModal(true)}
                  className="h-auto py-4 justify-start"
                >
                  <div className="flex items-start gap-3 text-left">
                    <Award className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div>
                      <h3 className="font-medium mb-1">Configure Achievements</h3>
                      <p className="text-sm text-gray-600">Set thresholds for professional and client achievements</p>
                    </div>
                  </div>
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </main>

      {/* Monitoring Config Modal */}
      <MonitoringConfigModal
        isOpen={showMonitoringModal}
        onClose={() => setShowMonitoringModal(false)}
      />

      {/* Achievement Config Modal */}
      <AchievementConfigModal
        isOpen={showAchievementModal}
        onClose={() => setShowAchievementModal(false)}
        achievementConfigs={achievementConfigs}
        selectedAchievementType={selectedAchievementType}
        setSelectedAchievementType={setSelectedAchievementType}
        dispatch={dispatch}
      />
    </div>
  );
};

export default ScoringSettings;
